import type { IChartDataSignal } from "../../scheme/components/analytics/interfaces";

export const VIBRATION_UNIT = {
  ACCELERATION: 0,
  VELOCITY: 1,
  DISPLACEMENT: 2,
} as const;

export type TVibrationUnitCode = (typeof VIBRATION_UNIT)[keyof typeof VIBRATION_UNIT];

const TWO_PI = 2 * Math.PI;
const MIN_FREQUENCY_HZ = 1e-9;
const MIN_WAVEFORM_SAMPLES = 3;
const DEFAULT_INTEGRATION_LOW_FREQ_HZ = 1;
const INTEGRATION_ZERO_BAND_HZ = 0.1;
const MIN_ZEROED_SPECTRUM_BINS = 5;
const WEAK_BIN_FALLBACK_FREQ_HZ = 4;
const SPECTRUM_ABS_MIN_MEANING = 0.01;
const SPECTRUM_AVERAGE_WINDOW_BINS = 10;

export const isVibrationUnitCode = (code: number | null | undefined): code is TVibrationUnitCode =>
  code === VIBRATION_UNIT.ACCELERATION || code === VIBRATION_UNIT.VELOCITY || code === VIBRATION_UNIT.DISPLACEMENT;

const isPow2 = (value: number): boolean => value > 0 && (value & (value - 1)) === 0;

const nextPow2 = (value: number): number => {
  let size = 1;

  while (size < value) {
    size <<= 1;
  }

  return size;
};

const fftRadix2 = (re: Float64Array, im: Float64Array): void => {
  const n = re.length;

  if (n <= 1) {
    return;
  }

  let j = 0;
  for (let i = 0; i < n - 1; i += 1) {
    if (i < j) {
      let temp = re[i];
      re[i] = re[j];
      re[j] = temp;
      temp = im[i];
      im[i] = im[j];
      im[j] = temp;
    }

    let k = n >> 1;
    while (k <= j) {
      j -= k;
      k >>= 1;
    }
    j += k;
  }

  for (let size = 2; size <= n; size <<= 1) {
    const half = size >>> 1;
    const wmCos = Math.cos(-TWO_PI / size);
    const wmSin = Math.sin(-TWO_PI / size);

    for (let k0 = 0; k0 < n; k0 += size) {
      let wCos = 1;
      let wSin = 0;

      for (let localIndex = k0; localIndex < k0 + half; localIndex += 1) {
        const idx = localIndex + half;
        const tr = wCos * re[idx] - wSin * im[idx];
        const ti = wCos * im[idx] + wSin * re[idx];
        re[idx] = re[localIndex] - tr;
        im[idx] = im[localIndex] - ti;
        re[localIndex] += tr;
        im[localIndex] += ti;

        const nextCos = wCos * wmCos - wSin * wmSin;
        wSin = wCos * wmSin + wSin * wmCos;
        wCos = nextCos;
      }
    }
  }
};

const ifftRadix2 = (re: Float64Array, im: Float64Array): void => {
  const n = re.length;

  for (let i = 0; i < n; i += 1) {
    im[i] *= -1;
  }

  fftRadix2(re, im);

  const inv = 1 / n;
  for (let i = 0; i < n; i += 1) {
    im[i] *= -inv;
    re[i] *= inv;
  }
};

const getComplexAbs = (re: Float64Array, im: Float64Array, index: number): number => Math.hypot(re[index], im[index]);

const getAverageSpectrumAbs = (bin: number, re: Float64Array, im: Float64Array): number => {
  const binCount = re.length;

  if (bin < 0 || bin >= binCount) {
    return 0;
  }

  let firstValid = 0;
  while (firstValid < binCount && getComplexAbs(re, im, firstValid) < SPECTRUM_ABS_MIN_MEANING) {
    firstValid += 1;
  }

  if (bin < firstValid) {
    return 0;
  }

  const minBin = Math.max(bin - SPECTRUM_AVERAGE_WINDOW_BINS, firstValid);
  const maxBin = Math.min(bin + SPECTRUM_AVERAGE_WINDOW_BINS, binCount - 1);
  const windowSize = maxBin - minBin + 1;
  let sum = 0;

  for (let index = minBin; index <= maxBin; index += 1) {
    const value = getComplexAbs(re, im, index);

    if (value > SPECTRUM_ABS_MIN_MEANING) {
      sum += value;
    }
  }

  return windowSize > 0 ? sum / windowSize : 0;
};

const applyIntegrationFactor = (re: Float64Array, im: Float64Array, bin: number, imagFactor: number): void => {
  const oldRe = re[bin];
  const oldIm = im[bin];

  re[bin] = oldIm * imagFactor;
  im[bin] = -oldRe * imagFactor;
};

const mirrorHermitianSpectrum = (re: Float64Array, im: Float64Array): void => {
  const n = re.length;
  const nyquistBin = n >> 1;

  for (let bin = 1; bin < nyquistBin; bin += 1) {
    re[n - bin] = re[bin];
    im[n - bin] = -im[bin];
  }
};

const scaleSignal = (signal: Float64Array, factor: number): void => {
  for (let index = 0; index < signal.length; index += 1) {
    signal[index] *= factor;
  }
};

const centerSignal = (signal: Float64Array): void => {
  const size = signal.length;

  if (size < MIN_WAVEFORM_SAMPLES) {
    return;
  }

  let average = 0;
  for (let index = 0; index < size; index += 1) {
    average += signal[index];
  }
  average /= size;

  let windowedSum = 0;
  const denom = size - 1;
  for (let index = 0; index < size; index += 1) {
    const hann = 0.5 - 0.5 * Math.cos((TWO_PI * index) / denom);
    windowedSum += hann * (signal[index] - average);
  }
  windowedSum /= size / 2;

  const offset = windowedSum + average;
  for (let index = 0; index < size; index += 1) {
    signal[index] -= offset;
  }
};

const integrate = (input: Float64Array, output: Float64Array, dtSec: number, lowFreqHz: number): boolean => {
  const size = input.length;

  if (size < MIN_WAVEFORM_SAMPLES || dtSec <= 0) {
    return false;
  }

  const fftSize = isPow2(size) ? size : nextPow2(size);
  const re = new Float64Array(fftSize);
  const im = new Float64Array(fftSize);

  re.set(input);
  fftRadix2(re, im);

  const freqStepHz = 1 / (dtSec * fftSize);
  const nyquistBin = fftSize >> 1;
  const binsToZero = Math.min(
    Math.max(Math.floor(INTEGRATION_ZERO_BAND_HZ / freqStepHz), MIN_ZEROED_SPECTRUM_BINS),
    nyquistBin,
  );
  const binsToFilter = Math.min(Math.floor(Math.max(1, lowFreqHz) / freqStepHz), nyquistBin);
  const validLevel = (0.01 * fftSize) / 4;
  const weakBinFactor = 1 / (TWO_PI * WEAK_BIN_FALLBACK_FREQ_HZ);

  re[0] = 0;
  im[0] = 0;

  for (let bin = 1; bin <= nyquistBin; bin += 1) {
    if (bin <= binsToZero) {
      re[bin] = 0;
      im[bin] = 0;
      continue;
    }

    const frequencyHz = bin * freqStepHz;
    let imagFactor = 1 / (TWO_PI * frequencyHz);

    if (bin <= binsToFilter) {
      const isSignificantPeak = getComplexAbs(re, im, bin) > getAverageSpectrumAbs(bin, re, im) + validLevel;
      imagFactor = isSignificantPeak ? 1 / (TWO_PI * frequencyHz) : weakBinFactor;
    }

    applyIntegrationFactor(re, im, bin, imagFactor);
  }

  mirrorHermitianSpectrum(re, im);
  ifftRadix2(re, im);
  output.set(re.subarray(0, size));

  return true;
};

const differentiate = (input: Float64Array, output: Float64Array, dtSec: number): boolean => {
  const size = input.length;

  if (size < MIN_WAVEFORM_SAMPLES || dtSec <= 0) {
    return false;
  }

  output[0] = 0;
  for (let index = 0; index < size - 1; index += 1) {
    output[index + 1] = (input[index + 1] - input[index]) / dtSec;
  }

  return true;
};

export const getSpectrumAmplitudeFactor = (
  sourceUnits: TVibrationUnitCode,
  targetUnits: TVibrationUnitCode,
  frequencyHz: number,
): number => {
  if (sourceUnits === targetUnits) {
    return 1;
  }

  if (frequencyHz <= MIN_FREQUENCY_HZ) {
    return 0;
  }

  const omega = TWO_PI * frequencyHz;

  switch (sourceUnits) {
    case VIBRATION_UNIT.ACCELERATION:
      if (targetUnits === VIBRATION_UNIT.VELOCITY) {
        return 1000 / omega;
      }

      return 1_000_000 / (omega * omega);
    case VIBRATION_UNIT.VELOCITY:
      if (targetUnits === VIBRATION_UNIT.ACCELERATION) {
        return omega / 1000;
      }

      return 1000 / omega;
    case VIBRATION_UNIT.DISPLACEMENT:
      if (targetUnits === VIBRATION_UNIT.VELOCITY) {
        return omega / 1000;
      }

      return (omega * omega) / 1_000_000;
    default:
      return 1;
  }
};

export const resolvePointFrequencyHz = (
  xValue: number,
  unitsXCode: number | null | undefined,
  rotationFrequencyRpm: number | null | undefined,
): number => {
  if (unitsXCode === 4) {
    const baseFrequencyHz =
      rotationFrequencyRpm !== null && rotationFrequencyRpm !== undefined && rotationFrequencyRpm > 0
        ? rotationFrequencyRpm / 60
        : null;

    if (baseFrequencyHz === null) {
      return 0;
    }

    return xValue * baseFrequencyHz;
  }

  return xValue;
};

const resolveWaveformSamplingFrequencyHz = (chart: IChartDataSignal): number | null => {
  const count = chart.items.length;

  if (count < 2) {
    return null;
  }

  const firstX = chart.items[0]?.x;
  const lastX = chart.items[count - 1]?.x;

  if (!Number.isFinite(firstX) || !Number.isFinite(lastX)) {
    return null;
  }

  const span = lastX - firstX;

  if (span <= 0) {
    return null;
  }

  if (chart.unitsXCode === 5) {
    return ((count - 1) * 1000) / span;
  }

  if (chart.unitsXCode === 6) {
    const rotationFrequencyRpm = chart.rotationFrequencyRpm;

    if (rotationFrequencyRpm === null || rotationFrequencyRpm === undefined || rotationFrequencyRpm <= 0) {
      return null;
    }

    return (count * rotationFrequencyRpm) / 60;
  }

  return null;
};

export const canConvertSignalChartUnits = (chart: IChartDataSignal): boolean => {
  if (!isVibrationUnitCode(chart.unitsYCode)) {
    return false;
  }

  if (chart.rangeType === 2) {
    return false;
  }

  if (chart.unitsXCode === 4 && (chart.rotationFrequencyRpm === null || chart.rotationFrequencyRpm === undefined)) {
    return false;
  }

  if ((chart.unitsXCode === 5 || chart.unitsXCode === 6) && resolveWaveformSamplingFrequencyHz(chart) === null) {
    return false;
  }

  return chart.unitsXCode === 3 || chart.unitsXCode === 4 || chart.unitsXCode === 5 || chart.unitsXCode === 6;
};

export const convertWaveformValuesUnits = (
  values: Float64Array,
  sourceUnits: TVibrationUnitCode,
  targetUnits: TVibrationUnitCode,
  dtSec: number,
  lowFreqHz: number = DEFAULT_INTEGRATION_LOW_FREQ_HZ,
): Float64Array => {
  if (sourceUnits === targetUnits || values.length < MIN_WAVEFORM_SAMPLES || dtSec <= 0) {
    return Float64Array.from(values);
  }

  const output = new Float64Array(values.length);
  let converted = false;

  switch (sourceUnits) {
    case VIBRATION_UNIT.ACCELERATION:
      if (targetUnits === VIBRATION_UNIT.VELOCITY) {
        converted = integrate(values, output, dtSec, lowFreqHz);
      } else {
        const tmp = new Float64Array(values.length);
        converted = integrate(values, tmp, dtSec, lowFreqHz);

        if (converted) {
          centerSignal(tmp);
          converted = integrate(tmp, output, dtSec, lowFreqHz);
        }
      }
      break;
    case VIBRATION_UNIT.VELOCITY:
      converted =
        targetUnits === VIBRATION_UNIT.ACCELERATION
          ? differentiate(values, output, dtSec)
          : integrate(values, output, dtSec, lowFreqHz);
      break;
    case VIBRATION_UNIT.DISPLACEMENT:
      if (targetUnits === VIBRATION_UNIT.ACCELERATION) {
        const tmp = new Float64Array(values.length);
        converted = differentiate(values, tmp, dtSec);

        if (converted) {
          centerSignal(tmp);
          converted = differentiate(tmp, output, dtSec);
        }
      } else {
        converted = differentiate(values, output, dtSec);
      }
      break;
    default:
      return Float64Array.from(values);
  }

  if (!converted) {
    return Float64Array.from(values);
  }

  centerSignal(output);

  if (sourceUnits === VIBRATION_UNIT.ACCELERATION) {
    scaleSignal(output, targetUnits === VIBRATION_UNIT.VELOCITY ? 1000 : 1_000_000);
  } else if (sourceUnits === VIBRATION_UNIT.VELOCITY) {
    scaleSignal(output, targetUnits === VIBRATION_UNIT.ACCELERATION ? 1 / 1000 : 1000);
  } else {
    scaleSignal(output, targetUnits === VIBRATION_UNIT.ACCELERATION ? 1 / 1_000_000 : 1 / 1000);
  }

  return output;
};

const convertSpectrumChartUnits = (
  chart: IChartDataSignal,
  sourceUnits: TVibrationUnitCode,
  targetUnits: TVibrationUnitCode,
): IChartDataSignal => ({
  ...chart,
  items: chart.items.map(item => {
    const yValue = typeof item.y === "number" ? item.y : item.y.real ?? 0;
    const frequencyHz = resolvePointFrequencyHz(item.x, chart.unitsXCode, chart.rotationFrequencyRpm);
    const factor = getSpectrumAmplitudeFactor(sourceUnits, targetUnits, frequencyHz);

    return {
      ...item,
      y: yValue * factor,
    };
  }),
});

const convertWaveformChartUnits = (
  chart: IChartDataSignal,
  sourceUnits: TVibrationUnitCode,
  targetUnits: TVibrationUnitCode,
): IChartDataSignal => {
  const samplingFrequencyHz = resolveWaveformSamplingFrequencyHz(chart);

  if (samplingFrequencyHz === null) {
    return chart;
  }

  const values = Float64Array.from(chart.items, item => (typeof item.y === "number" ? item.y : item.y.real ?? 0));
  const converted = convertWaveformValuesUnits(values, sourceUnits, targetUnits, 1 / samplingFrequencyHz);

  return {
    ...chart,
    items: chart.items.map((item, index) => ({
      ...item,
      y: converted[index],
    })),
  };
};

export const convertSignalChartUnits = (
  chart: IChartDataSignal,
  targetUnits: TVibrationUnitCode,
  formatUnitLabel: (unitCode: number) => string,
): IChartDataSignal => {
  const sourceUnits = chart.unitsYCode;

  if (!isVibrationUnitCode(sourceUnits) || sourceUnits === targetUnits || !canConvertSignalChartUnits(chart)) {
    return chart;
  }

  const convertedChart =
    chart.unitsXCode === 3 || chart.unitsXCode === 4
      ? convertSpectrumChartUnits(chart, sourceUnits, targetUnits)
      : convertWaveformChartUnits(chart, sourceUnits, targetUnits);

  return {
    ...convertedChart,
    unitsYCode: targetUnits,
    unitsY: formatUnitLabel(targetUnits),
  };
};

export const resolveNativeSignalDisplayUnit = (charts: readonly IChartDataSignal[]): TVibrationUnitCode | null => {
  for (const chart of charts) {
    if (isVibrationUnitCode(chart.unitsYCode)) {
      return chart.unitsYCode;
    }
  }

  return null;
};

export const canConvertAnySignalChartUnits = (charts: readonly IChartDataSignal[]): boolean =>
  charts.some(chart => canConvertSignalChartUnits(chart));
